/**
 * Notification Route Resolver
 *
 * Maps notification_type + data fields to a frontend URL.
 * Used by: service worker, foreground toast handler, Capacitor native handler.
 *
 * To add a new notification type, add an entry to NOTIFICATION_ROUTES below.
 */

type NotificationData = Record<string, string>;

interface RouteConfig {
  /** The data field key that contains the resource ID */
  dataKey?: string;
  /** Route template — use {id} as placeholder for the data value */
  route: string;
}

/**
 * Route mapping for each notification type.
 * Add new entries here when adding push notifications to new modules.
 */
const NOTIFICATION_ROUTES: Record<string, RouteConfig> = {
  chat: { dataKey: 'conversation_id', route: '/chat/{id}' },
  alarm: { dataKey: 'alarm_id', route: '/alarms/{id}' },
  access_event: { dataKey: 'access_code_id', route: '/access-control/{id}' },
  task: { route: '/tasks' },
  appointment: { dataKey: 'appointment_id', route: '/calendar/event/{id}' },
  vulnerability: { route: '/dashboard' },
  system: { route: '/dashboard' },
};

const DEFAULT_ROUTE = '/dashboard';

/**
 * Resolve a notification to a frontend URL based on its type and data.
 */
export function resolveNotificationUrl(
  notificationType: string | undefined,
  data: NotificationData = {},
): string {
  if (!notificationType) return DEFAULT_ROUTE;

  const config = NOTIFICATION_ROUTES[notificationType];
  if (!config) return DEFAULT_ROUTE;

  if (config.dataKey) {
    const id = data[config.dataKey];
    if (id && id !== 'deleted') {
      return config.route.replace('{id}', id);
    }
  }

  // If no dataKey needed or ID not found, return the route without substitution
  return config.route.replace('/{id}', '');
}
